import { Component, OnInit, OnDestroy, ViewChild, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { of, Subject } from 'rxjs';
import { map, catchError, switchMap, debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { FilterService } from 'primeng/api';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { Product } from 'src/app/shared/product';
import { ProductApiService} from 'src/app/services/product-api.service';

/**
 * Cleans a typed/pasted search string before it is used as the search pattern.
 * `String.trim()` alone leaves zero-width characters and soft hyphens (common when copying a
 * part number from a PDF or web page) and the server-side word splitter only splits on a plain
 * space, so a tab/newline/NBSP inside the text would glue two words into one token.
 */
export function normalizeSearchString(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/[\u200B-\u200D\u2060\u00AD\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Modal dialog for full-text product search with configurable search mode flags.
 *
 * Data-flow: user types → `onKeyPress` → `searchStr$.next(...)` →
 * debounced 300 ms → `switchMap` cancels any in-flight request and calls
 * `ProductApiService.getProductSimpleSearch(str, old, com)` →
 * `products` array populated → `app-list-product-array` renders results →
 * user selects row → `onProductSelection` sets `selection` →
 * `onConfirm` emits via `selectedProduct`.
 *
 * `switchMap` handles overlapping requests: a new keystroke cancels the previous
 * in-flight HTTP call automatically. `loading` drives the spinner/button toggle.
 *
 * Key collaborator: `ProductApiService.getProductSimpleSearch(str, old, com)`.
 *
 * Inputs:
 *  - `header`       — dialog title text
 *  - `display`      — two-way binding controlling dialog visibility
 *  - `searchText`   — pre-populated search string
 *  - `allowOld`     — when false, obsolete-product checkbox is disabled and forced off
 *  - `allowComplex` — when false, complex/bundle checkbox is disabled and forced off
 *  - `searchLabel`  — prompt label above the search input field
 *
 * Outputs:
 *  - `selectedProduct` — emits confirmed `Product` when user clicks ОК
 *  - `displayChange`   — emits `false` when dialog is hidden (supports `[(display)]`)
 */
@Component({
  selector: 'app-products-search',
  templateUrl: './products-search.component.html',
  styleUrls: ['./products-search.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ProductsSearchComponent implements OnInit, OnChanges, OnDestroy {

  private globalSettinsData: GlobalSettingsData;

  private readonly destroy$ = new Subject<void>();

  products: Product[] = [];
  selection: Product = null;

  displayPS: boolean = false;

  modal: boolean = true;
  loading: boolean = false;

  rows: number = 10;

  something: string;

  /** Toggles the filter toolbar visibility in the template. */
  showToolBar: boolean = false;

  searchOld: boolean = false;
  searchComplex: boolean = true;
  showStock: boolean = true;

  /** Filter array passed to `app-list-product-array`; reflects `searchOld` and `searchComplex` checkbox state. */
  showObsoleteProducts: number[] = [0, 2];

  /** Column set passed to `app-list-product-array` defining which columns to render. */
  resultColumnSet: string[] = [
    'PRODCODE',
    'PRODNAME',
    'PRODARTICUL',
    'PRODCUSTCODE',
    'PRODGROUPNAME',
    'PRODVENDORNAME',
    'PRODPRICEUAH',
    'PRODPRICEUSD',
    // TODO(B-18): competitor price columns — not yet available from BatchInfo
    'PRODSTOCK'
  ];

  /** True when no row is selected; disables the ОК button. */
  get noSelection(): boolean { return this.selection == null; }

  /** Dialog header title text. */
  @Input() header: string = "Поиск товаров";

  /** Two-way binding controlling dialog visibility (`[(display)]`). */
  @Input() display: boolean = false;

  /** Pre-populated search string; synced to `something` on change. */
  @Input() searchText: string = '';

  /** When `false`, the "search obsolete" checkbox is disabled and forced off. */
  @Input() allowOld: boolean = true;
  /** When `false`, the "search complex/bundle" checkbox is disabled and forced off. */
  @Input() allowComplex: boolean = true;

  /** Prompt label rendered above the search input field. */
  @Input() searchLabel: string = 'Что нибудь...';

  /** Emits the confirmed `Product` when the user clicks ОК. */
  @Output() selectedProduct: EventEmitter<Product> = new EventEmitter();

  /** Emits `false` when dialog is hidden — supports two-way `[(display)]` binding. */
  @Output() displayChange = new EventEmitter();

  private searchStr$: Subject<{ str: string; old: boolean; com: boolean }> = new Subject<{ str: string, old: boolean, com: boolean }>();

  constructor(
    private prodApi: ProductApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private filterService: FilterService
  ) { }

  /**
   * Reacts to `@Input` changes:
   * - `display` → triggers search push when dialog opens.
   * - `searchText` → syncs value to `something` (the search input model).
   * - `allowOld` / `allowComplex` → caps the corresponding boolean flags.
   */
  ngOnChanges(changes: SimpleChanges) {
    for (const propName of Object.keys(changes)) {
      const change = changes[propName];
      environment.production || console.log('*** ProductsSearchComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));

      switch (propName) {

        case 'allowOld':
          this.searchOld = this.searchOld && (change.currentValue ?? false);
          break;

        case 'allowComplex':
          this.searchComplex = this.searchComplex && change.currentValue;
          break;

        case 'searchText':
          this.something = this.searchText;
          break;

        case 'display':
          if (change.currentValue) {
            this.pushSearch();
          }
          break;
      }
    }
  }

  /**
   * Registers custom PrimeNG `FilterService` comparators (`gtOrZero`, `GteOrZero`)
   * and sets up the debounced `searchStr$` → `getProductSimpleSearch` pipeline.
   * `switchMap` automatically cancels any in-flight request when a new value arrives.
   */
  ngOnInit(): void {

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter === 0) && (value === 0)) || ((filter > 0) && (value > filter))
    ));

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter === 0) && (value === 0)) || ((filter > 0) && (value >= filter))
    ));

    this.searchStr$.pipe(
      debounceTime(300),
      filter(opt => opt && (opt.str || '').length > 3),
      distinctUntilChanged((a, b) => a.str === b.str && a.old === b.old && a.com === b.com),
      switchMap(opt => {
        this.loading = true;
        this.products = [];
        return this.prodApi.getProductSimpleSearch(opt.str, opt.old, opt.com).pipe(
          map(data => {
            this.products = data;
            this.loading = false;
          }),
          catchError(error => {
            console.error('*** ProductsSearchComponent search error:', error);
            this.products = [];
            this.loading = false;
            return of(null);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  /**
   * Pushes current search params to `searchStr$` if the cleaned input
   * (`normalizeSearchString`) meets the minimum length (> 3 chars). `debounceTime` and `switchMap` in the pipeline handle
   * rate-limiting and in-flight request cancellation respectively.
   */
  pushSearch(): void {
    const str = normalizeSearchString(this.something);
    if (str.length > 3) {
      this.searchStr$.next({
        str,
        old: this.searchOld,
        com: this.searchComplex
      });
    }
  }

  /** Completes `destroy$` to cancel the search pipeline subscription. */
  ngOnDestroy(): void {
    environment.production || console.log('*** ProductsSearchComponent in ngOnDestroy');
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Debug hook for the dialog open event. */
  onShowDialog() {
    environment.production || console.log('*** ProductsSearchComponent onShow: ');
  }

  /** Emits `displayChange(false)` to close the dialog via two-way binding. */
  onHideDialog() {
    environment.production || console.log('*** ProductsSearchComponent onHide');
    this.displayChange.emit(false);
  }

  /** Emits `selectedProduct` and closes the dialog if a row is selected. */
  onConfirm(): void {
    if (!this.noSelection) {
      this.selectedProduct.emit(this.selection);
      this.displayChange.emit(false);
    }
  }

  /** Search button click handler — pushes current input to the search pipeline. */
  onSearch(_e: unknown): void {
    environment.production || console.log('*** ProductsSearchComponent onSearch');
    this.pushSearch();
  }

  /** Handles `(listIsReady)` event from `app-list-product-array`; debug hook. */
  onSearchEnd(_e: unknown): void {
    environment.production || console.log('*** ProductsSearchComponent onSearchEnd');
  }

  /** Handles `(productSelected)` event from `app-list-product-array`; sets `selection`. */
  onProductSelection(event: { data: Product }): void {
    environment.production || console.log('*** ProductsSearchComponent onProductSelection: ' + JSON.stringify(event));
    if (event && event.data) {
      this.selection = event.data;
    }
  }

  /**
   * Rebuilds `showObsoleteProducts` filter array from current checkbox state
   * and re-triggers the search pipeline.
   */
  onCheckBoxClick(_e: unknown): void {
    this.showObsoleteProducts = [0];
    if (this.searchOld) this.showObsoleteProducts.push(1);
    if (this.searchComplex) this.showObsoleteProducts.push(2);
    this.pushSearch();
  }

  /** Keyup handler on the search input — pushes current input to the search pipeline. */
  onKeyPress(_e: unknown): void {
    environment.production || console.log('*** ProductsSearchComponent onKeyPress');
    this.pushSearch();
  }
}
